import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Paper,
} from '@mui/material';
import {
  Search,
  Assignment,
  Security,
  Payment,
  Star,
  LocationOn,
  Build,
  Computer,
  LocalShipping,
  Home as HomeIcon,
  School,
  BusinessCenter,
  Construction,
  DesignServices,
  HealthAndSafety,
  Gavel,
  TheaterComedy,
} from '@mui/icons-material';
import { resolveCategoryCanonicalKey, translateCategory } from '../utils/categoryTranslations';
import categoryService, { Category } from '../services/categoryService';
import { useAuth } from '../contexts/AuthContext';

type FeaturedCategoryCard = {
  key: string;
  fallbackName: string;
  canonicalCandidates: string[];
};

const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);

  const featuredCategoryCards: FeaturedCategoryCard[] = useMemo(
    () => [
      { key: 'handyman', fallbackName: t('home.categories.handyman'), canonicalCandidates: ['construction'] },
      { key: 'cleaning', fallbackName: t('home.categories.cleaning'), canonicalCandidates: ['cleaning'] },
      { key: 'it', fallbackName: t('home.categories.it'), canonicalCandidates: ['it_services', 'computer_services'] },
      { key: 'moving', fallbackName: t('home.categories.moving'), canonicalCandidates: ['transport_logistics', 'transportation'] },
      // Ремонти -> най-близко към "construction"
      { key: 'renovation', fallbackName: t('home.categories.renovation'), canonicalCandidates: ['construction'] },
      { key: 'tutoring', fallbackName: t('home.categories.tutoring'), canonicalCandidates: ['education'] },
    ],
    [t]
  );

  const categoryColors = useMemo(
    () => ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD', '#D4A5A5', '#7E57C2', '#26A69A', '#EF5350', '#5C6BC0'],
    []
  );

  const resolveCategoryIcon = (iconName?: string) => {
    const normalized = (iconName || '').toLowerCase();
    switch (normalized) {
      case 'construction':
        return <Construction />;
      case 'computer':
        return <Computer />;
      case 'business':
        return <BusinessCenter />;
      case 'school':
        return <School />;
      case 'design_services':
      case 'design':
        return <DesignServices />;
      case 'health_and_safety':
        return <HealthAndSafety />;
      case 'home':
        return <HomeIcon />;
      case 'local_shipping':
        return <LocalShipping />;
      case 'gavel':
        return <Gavel />;
      case 'theater_comedy':
        return <TheaterComedy />;
      default:
        return <Build />;
    }
  };

  const handleCategoryClick = (categoryId?: number, fallbackSearch?: string) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const userType = user?.user_type || localStorage.getItem('user_type') || 'client';
    if (userType === 'professional') {
      if (categoryId) {
        navigate(`/tasks?category_id=${categoryId}`);
        return;
      }
      if (fallbackSearch) {
        navigate(`/tasks?search=${encodeURIComponent(fallbackSearch)}`);
      } else {
        navigate('/tasks');
      }
      return;
    }

    // За клиенти е по-логично да започнат създаване на задача в избраната категория.
    if (categoryId) {
      navigate(`/tasks/create?category_id=${categoryId}`);
      return;
    }
    navigate('/tasks/create');
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryService.getCategories(undefined, 0, 10);
        setCategories(response.items || []);
      } catch (error) {
        console.error('Error loading homepage categories:', error);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  const displayedCategories = useMemo(() => {
    const usedCategoryIds = new Set<number>();

    const matchedCards = featuredCategoryCards.map((card) => {
      const matchedCategory = categories.find((category) => {
        if (usedCategoryIds.has(category.id)) {
          return false;
        }

        const canonical = resolveCategoryCanonicalKey(category.name);
        return canonical ? card.canonicalCandidates.includes(canonical) : false;
      });

      if (matchedCategory?.id) {
        usedCategoryIds.add(matchedCategory.id);
      }

      return {
        cardKey: card.key,
        id: matchedCategory?.id,
        iconName: matchedCategory?.icon,
        name: matchedCategory?.name || card.fallbackName,
        fallbackSearch: card.fallbackName,
      };
    });

    return matchedCards.slice(0, 6);
  }, [categories, featuredCategoryCards]);

  const features = [
    {
      icon: <Search />,
      title: t('home.features.search.title'),
      description: t('home.features.search.description'),
    },
    {
      icon: <Security />,
      title: t('home.features.security.title'),
      description: t('home.features.security.description'),
    },
    {
      icon: <Payment />,
      title: t('home.features.payment.title'),
      description: t('home.features.payment.description'),
    },
  ];

  const testimonials = [
    {
      name: 'Мария Иванова',
      role: t('home.testimonials.client'),
      content: t('home.testimonials.client_content'),
      avatar: 'https://randomuser.me/api/portraits/women/32.jpg',
      rating: 5,
    },
    {
      name: 'Георги Петров',
      role: t('home.testimonials.professional'),
      content: t('home.testimonials.professional_content'),
      avatar: 'https://randomuser.me/api/portraits/men/45.jpg',
      rating: 5,
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
          color: 'white',
          py: 8,
          position: 'relative',
          overflow: 'hidden',
          mb: 6,
        }}
      >
        {/* Декоративен елемент - кръг в горната дясна част */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }}
        />
        
        {/* Декоративен елемент - малки точки */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            width: 200,
            height: 200,
            opacity: 0.2,
            background: 'radial-gradient(white 3px, transparent 4px)',
            backgroundSize: '20px 20px',
          }}
        />
        
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6} sx={{ position: 'relative', zIndex: 1 }}>
              <Box
                component="img"
                src="/brand/logo-full.svg"
                alt="MasterBook"
                sx={{
                  width: { xs: 220, sm: 280, md: 320 },
                  height: 'auto',
                  mb: 2,
                }}
              />
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 'bold',
                  textShadow: '1px 1px 3px rgba(0,0,0,0.2)',
                  mb: 3
                }}
              >
                {t('home.hero.title')}
              </Typography>
              <Typography 
                variant="h5" 
                paragraph
                sx={{ 
                  mb: 4,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                  maxWidth: '90%'
                }}
              >
                {t('home.hero.subtitle')}
              </Typography>
              <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/tasks/create')}
                  sx={{ 
                    py: 1.5,
                    px: 4,
                    fontWeight: 'bold',
                    borderRadius: 2,
                    backgroundColor: 'white',
                    color: '#2196F3',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 15px rgba(0,0,0,0.1)'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  {t('home.hero.post_task')}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  color="inherit"
                  onClick={() => navigate('/tasks')}
                  sx={{ 
                    py: 1.5,
                    px: 4,
                    fontWeight: 'bold',
                    borderRadius: 2,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 15px rgba(0,0,0,0.1)'
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  {t('home.hero.browse_tasks')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <Box
                component="img"
                src="/brand/logo-symbol.svg"
                alt="MasterBook symbol"
                sx={{
                  width: '100%',
                  maxWidth: 300,
                  height: 'auto',
                  filter: 'drop-shadow(0 10px 18px rgba(0,0,0,0.15))',
                  animation: 'float 3s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': {
                      transform: 'translateY(0)',
                    },
                    '50%': {
                      transform: 'translateY(-10px)',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('home.features.title')}
        </Typography>
        <Typography 
          variant="h6" 
          component="p" 
          align="center" 
          color="textSecondary" 
          sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}
        >
          {t('home.features.subtitle', 'Нашата платформа предлага всичко необходимо за лесно намиране и управление на услуги')}
        </Typography>
        
        <Grid container spacing={4} sx={{ mt: 2 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ 
                height: '100%', 
                borderRadius: 3, 
                transition: 'all 0.3s ease',
                boxShadow: '0 5px 15px rgba(0,0,0,0.05)',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                  '& .feature-icon': {
                    transform: 'rotateY(180deg)'
                  }
                },
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: -20, 
                    right: -20, 
                    width: 100, 
                    height: 100, 
                    borderRadius: '50%',
                    background: 'rgba(33, 150, 243, 0.05)' 
                  }} 
                />
                
                <CardContent sx={{ p: 4, zIndex: 1, position: 'relative' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Avatar
                      className="feature-icon"
                      sx={{
                        width: 70,
                        height: 70,
                        bgcolor: 'primary.main',
                        boxShadow: '0 5px 15px rgba(33, 150, 243, 0.2)',
                        transition: 'transform 0.6s',
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                  </Box>
                  <Typography 
                    variant="h5" 
                    component="h3" 
                    align="center" 
                    gutterBottom
                    sx={{ fontWeight: 'bold', mb: 2 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    color="textSecondary" 
                    align="center"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How it Works Section */}
      <Box sx={{ 
        bgcolor: 'grey.50', 
        py: 10,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Декоративни елементи */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: '20%',
            left: 40,
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: 'rgba(33, 150, 243, 0.1)'
          }} 
        />
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: '15%',
            right: 100,
            width: 120,
            height: 120,
            borderRadius: '50%',
            backgroundColor: 'rgba(33, 150, 243, 0.1)'
          }} 
        />
        
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ position: 'relative', zIndex: 1 }}
          >
            {t('home.how_it_works.title')}
          </Typography>
          <Typography 
            variant="h6" 
            component="p" 
            align="center" 
            color="textSecondary" 
            sx={{ mb: 8, maxWidth: 700, mx: 'auto', position: 'relative', zIndex: 1 }}
          >
            {t('home.how_it_works.subtitle', 'Опростен процес в три лесни стъпки')}
          </Typography>
          
          <Grid container spacing={6} sx={{ mt: 2, position: 'relative' }}>
            {/* Свързваща линия между елементите */}
            <Box 
              sx={{ 
                position: 'absolute',
                top: '40%',
                left: '10%',
                right: '10%',
                height: 3,
                backgroundColor: 'primary.light',
                zIndex: 0,
                display: { xs: 'none', md: 'block' }
              }} 
            />
            
            <Grid item xs={12} md={4}>
              <Box 
                textAlign="center" 
                sx={{ 
                  position: 'relative', 
                  zIndex: 1,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-10px)'
                  }
                }}
              >
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 100,
                    height: 100, 
                    borderRadius: '50%', 
                    backgroundColor: 'white',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    mx: 'auto',
                    mb: 3,
                    position: 'relative'
                  }}
                >
                  <Assignment sx={{ fontSize: 50, color: 'primary.main' }} />
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    1
                  </Box>
                </Box>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ fontWeight: 'bold', mb: 2 }}
                >
                  {t('home.how_it_works.step1.title')}
                </Typography>
                <Typography 
                  color="textSecondary"
                  sx={{ maxWidth: 280, mx: 'auto' }}
                >
                  {t('home.how_it_works.step1.description')}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box 
                textAlign="center" 
                sx={{ 
                  position: 'relative', 
                  zIndex: 1,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-10px)'
                  }
                }}
              >
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 100,
                    height: 100, 
                    borderRadius: '50%', 
                    backgroundColor: 'white',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    mx: 'auto',
                    mb: 3,
                    position: 'relative'
                  }}
                >
                  <Star sx={{ fontSize: 50, color: 'primary.main' }} />
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    2
                  </Box>
                </Box>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ fontWeight: 'bold', mb: 2 }}
                >
                  {t('home.how_it_works.step2.title')}
                </Typography>
                <Typography 
                  color="textSecondary"
                  sx={{ maxWidth: 280, mx: 'auto' }}
                >
                  {t('home.how_it_works.step2.description')}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box 
                textAlign="center" 
                sx={{ 
                  position: 'relative', 
                  zIndex: 1,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-10px)'
                  }
                }}
              >
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: 100,
                    height: 100, 
                    borderRadius: '50%', 
                    backgroundColor: 'white',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    mx: 'auto',
                    mb: 3,
                    position: 'relative'
                  }}
                >
                  <LocationOn sx={{ fontSize: 50, color: 'primary.main' }} />
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    3
                  </Box>
                </Box>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ fontWeight: 'bold', mb: 2 }}
                >
                  {t('home.how_it_works.step3.title')}
                </Typography>
                <Typography 
                  color="textSecondary"
                  sx={{ maxWidth: 280, mx: 'auto' }}
                >
                  {t('home.how_it_works.step3.description')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Button 
              variant="contained" 
              color="primary" 
              size="large"
              onClick={() => navigate('/tasks/create')}
              sx={{
                borderRadius: 2,
                px: 5,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: '0 5px 15px rgba(33, 150, 243, 0.3)',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(33, 150, 243, 0.4)',
                  transform: 'translateY(-3px)'
                },
                transition: 'all 0.3s'
              }}
            >
              {t('home.how_it_works.get_started', 'Започнете сега')}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Categories Section */}
      <Container maxWidth="lg" sx={{ my: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('home.categories.title')}
        </Typography>
        <Typography 
          variant="h6" 
          component="p" 
          align="center" 
          color="textSecondary" 
          sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}
        >
          {t('home.categories.subtitle', 'Разгледайте нашите популярни категории и намерете точно тези услуги, от които имате нужда')}
        </Typography>
        
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {displayedCategories.map((category, index) => (
            <Grid item xs={6} sm={4} md={2} key={`${category.cardKey}-${category.id || index}`}>
              <Paper
                sx={{
                  p: 0,
                  borderRadius: 3,
                  overflow: 'hidden',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 6px 15px rgba(0,0,0,0.07)',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 20px rgba(0,0,0,0.1)',
                    '& .category-icon-bg': {
                      transform: 'scale(1.1)'
                    }
                  },
                }}
                onClick={() => handleCategoryClick(category.id, category.fallbackSearch)}
              >
                <Box 
                  className="category-icon-bg"
                  sx={{ 
                    p: 3, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    backgroundColor: categoryColors[index % categoryColors.length],
                    color: 'white',
                    transition: 'transform 0.3s'
                  }}
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.3)', 
                      width: 56, 
                      height: 56,
                      mb: 1,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    {resolveCategoryIcon(category.iconName)}
                  </Avatar>
                </Box>
                
                <Box sx={{ p: 2 }}>
                  <Typography 
                    sx={{ 
                      fontWeight: 'bold',
                      fontSize: '0.95rem'
                    }}
                  >
                    {translateCategory(category.name, i18n.language)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            size="large"
            onClick={() => navigate('/tasks')}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(33, 150, 243, 0.04)'
              }
            }}
          >
            {t('home.categories.view_all', 'Разгледайте всички категории')}
          </Button>
        </Box>
      </Container>

      {/* Testimonials Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ mb: 2 }}
          >
            {t('home.testimonials.title')}
          </Typography>
          <Typography 
            variant="h6" 
            component="p" 
            align="center" 
            color="textSecondary" 
            sx={{ mb: 6, maxWidth: 700, mx: 'auto' }}
          >
            {t('home.testimonials.subtitle', 'Вижте какво казват нашите потребители за MasterBook')}
          </Typography>
          
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.07)',
                    transition: 'all 0.3s',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Кавички за декорация */}
                    <Box 
                      sx={{ 
                        position: 'absolute',
                        top: 30,
                        right: 30,
                        fontSize: '4rem',
                        color: 'primary.light',
                        opacity: 0.2,
                        fontFamily: 'serif',
                        fontWeight: 'bold',
                        lineHeight: 0.7
                      }}
                    >
                      "
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} sx={{ color: '#FFD700' }} />
                        ))}
                      </Box>
                      <Typography
                        paragraph
                        sx={{
                          fontSize: '1.1rem',
                          fontStyle: 'italic',
                          lineHeight: 1.6,
                          mb: 4
                        }}
                      >
                        "{testimonial.content}"
                      </Typography>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      borderTop: 1, 
                      borderColor: 'grey.100',
                      pt: 3
                    }}>
                      <Avatar
                        src={testimonial.avatar}
                        sx={{ 
                          width: 60, 
                          height: 60, 
                          mr: 2,
                          border: '3px solid white',
                          boxShadow: '0 5px 15px rgba(0,0,0,0.08)'
                        }}
                      />
                      <Box>
                        <Typography 
                          variant="h6"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {testimonial.name}
                        </Typography>
                        <Typography color="textSecondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Call to Action Section */}
      <Box 
        sx={{ 
          py: 10, 
          background: 'linear-gradient(135deg, #2196F3 30%, #21CBF3 90%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Декоративни елементи */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '5%',
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          }}
        />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold',
              textShadow: '1px 1px 3px rgba(0,0,0,0.2)',
              mb: 3
            }}
          >
            {t('home.cta.title', 'Готови ли сте да започнете?')}
          </Typography>
          <Typography 
            variant="h6" 
            paragraph
            sx={{ 
              mb: 5,
              maxWidth: 700,
              mx: 'auto',
              opacity: 0.9
            }}
          >
            {t('home.cta.description', 'Присъединете се към хилядите доволни потребители, които използват нашата платформа всеки ден')}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{ 
                py: 1.5,
                px: 4,
                fontWeight: 'bold',
                borderRadius: 2,
                backgroundColor: 'white',
                color: '#2196F3',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 15px rgba(0,0,0,0.1)'
                },
                transition: 'all 0.3s'
              }}
            >
              {t('home.cta.register', 'Регистрирайте се безплатно')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              color="inherit"
              onClick={() => navigate('/tasks')}
              sx={{ 
                py: 1.5,
                px: 4,
                fontWeight: 'bold',
                borderRadius: 2,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 15px rgba(0,0,0,0.1)'
                },
                transition: 'all 0.3s'
              }}
            >
              {t('home.cta.explore', 'Разгледайте задачи')}
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage; 